/**
 * The smallest shared pieces of type on the felt.
 *
 * Suit glyphs come from the platform symbol font with a negative side bearing
 * (see `.suit` in ui.css) so `8♥` sets tight; the colour is a token, because the
 * four suit inks are tuned for midnight felt and are overridden again on brass.
 */
import type { ReactNode } from 'react'
import type { Suit } from '@cards/shared'
import { SUIT_CLASS, SUIT_GLYPH } from '../cards/assets'

/** A bot, as Trickster marks one: a small laptop ahead of the name rather than
 *  the word "bot" in the line under it — which was a second line of type on
 *  every seat, saying something a glance at the name can carry. Line-drawn in
 *  the name's own colour, so it takes the brass with the name on its turn. */
export function BotIcon(): JSX.Element {
  return (
    <svg className="bot-icon" viewBox="0 0 16 12" role="img" aria-label="bot">
      <rect x="2.5" y="1" width="11" height="7.5" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M0.5 10.2h15l-1 1.3h-13z" fill="currentColor" />
    </svg>
  )
}

export function Glyph({ suit, style }: { suit: Suit; style?: React.CSSProperties }): JSX.Element {
  return (
    <span className={`suit ${SUIT_CLASS[suit]}`} style={style}>
      {SUIT_GLYPH[suit]}
    </span>
  )
}

/** `8♥`, `Misère` — a strain that may be NT renders as plain narrow type. */
export function Strainy({ strain, glyphSize }: { strain: string; glyphSize?: number }): JSX.Element {
  if (strain === 'NT') return <span>NT</span>
  const suit = strain as Suit
  return <Glyph suit={suit} style={glyphSize ? { fontSize: glyphSize } : undefined} />
}

const GLYPH_TO_SUIT: Record<string, Suit> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }

/** Server-written sentences (`explain`, ledger lines) carry bare suit glyphs.
 *  They must still read as their suit's ink, so the text is split on them. */
export function SuitText({ text }: { text: string }): JSX.Element {
  const parts = text.split(/([♠♥♦♣])/)
  return (
    <span>
      {parts.map((part, i) => {
        const suit = GLYPH_TO_SUIT[part]
        return suit ? <Glyph key={i} suit={suit} /> : <span key={i}>{part}</span>
      })}
    </span>
  )
}

export function Menu({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button className="menu" aria-label="Menu" onClick={onClick}>
      <svg viewBox="0 0 20 20" fill="currentColor">
        <circle cx="4" cy="10" r="1.6" />
        <circle cx="10" cy="10" r="1.6" />
        <circle cx="16" cy="10" r="1.6" />
      </svg>
    </button>
  )
}

/** Scores are written the way a scorer writes them: word, then number, right
 *  aligned, with minus in red pencil. */
export function ScorePad({ us, them }: { us: number; them: number }): JSX.Element {
  return (
    <div className="score" style={{ marginRight: 30 }}>
      <span>Us</span>
      <b className={us < 0 ? 'minus' : undefined}>{signed(us)}</b>
      <span>Them</span>
      <b className={them < 0 ? 'minus' : undefined}>{signed(them)}</b>
    </div>
  )
}

/** U+2212, not a hyphen: it aligns with the tabular figures. */
export function signed(n: number): string {
  return n < 0 ? `−${Math.abs(n)}` : String(n)
}

export function plusMinus(n: number): string {
  if (n < 0) return `−${Math.abs(n)}`
  return `+${n}`
}

const WORDS = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
]

/** Counts in sentences are words, not digits — digits are for the scorepad. */
export function countWord(n: number): string {
  return WORDS[n] ?? String(n)
}

export function Track({
  dots,
  goal,
  compact,
  label,
  fresh,
}: {
  dots: readonly ('us' | 'them' | null)[]
  goal: number | null
  compact?: boolean
  label: string
  /** The dot that has just filled, so it pops once rather than every render. */
  fresh?: number | null
}): JSX.Element {
  return (
    <div className={`track${compact ? ' compact' : ''}`} aria-label={label}>
      {dots.map((d, i) => (
        <i
          key={i}
          className={[d ?? '', goal !== null && i === goal - 1 ? 'goal' : '', fresh === i ? 'fresh' : '']
            .join(' ')
            .trim()}
        />
      ))}
    </div>
  )
}

export function Sentence({ children }: { children: ReactNode }): JSX.Element {
  return <p>{children}</p>
}
